import type { ReactNode } from "react"

import { Card } from "@/components/ui/card"
import { cn } from "@/lib/utils"

/**
 * OKS-527 — §14 goal-board section card (EARNED · N / TO UNLOCK · M).
 *
 * A rounded `Card` with an uppercase section header ("{title} · {count}")
 * over a responsive medallion grid: 2-up on mobile, 4-up from `sm`, and
 * back to 2-up from `lg` (OKS-972 — see below; the `lg` step is a
 * REDUCTION, which is the counter-intuitive part). When `count` is 0 and
 * an `emptyMessage` is supplied, renders the one-line encouraging message
 * instead of the grid (UX Spec §3 empty state).
 *
 * OKS-926 — the mobile step was 3-col at every width below `sm`, giving a
 * ~77px tile at 320px, which sliced most shipped names mid-glyph. Note
 * this `Card` pays its own `p-4`, so a catalogue tile is ~17px narrower
 * than a /profile preview tile at the same viewport — which is why the
 * catalogue, not the preview, is the binding case for the line budget.
 *
 * OKS-972 — why `lg` goes DOWN to 2. The creator shell caps the app at a
 * centred 480px column from `lg` up (`CREATOR_APP_COLUMN`, OKS-739), so
 * from `lg` the grid divides a FIXED 480px, not the viewport: 5 columns
 * gave a 73.2px tile — narrower than the same tile on a 320px phone
 * (121.0px) — and clipped 4 of 13 bounded names at 1024/1280/1440 while
 * clipping none below `lg`. Column count and column cap compose; neither
 * OKS-527 nor OKS-739 was wrong alone. 2-up restores a 201.0px tile.
 * ⚠️ Tile width does NOT track viewport width on this surface, so there is
 * no single worst viewport to reason toward — enumerate breakpoints.
 *
 * PROVENANCE: tile widths are read directly off the running app (320px:
 * catalogue 76.7px pre-fix, 121.0px post-fix; preview 138.0px post-fix).
 * Content-box widths ARE quotable: box = tile − padding − border (`p-2`
 * 16 + 2 below `sm`; `sm:p-4` 32 + 2 above), which reproduces every
 * measured point exactly.
 * ⚠️ An earlier version of this note attributed the ~2px to "the flex
 * column" and computed box as tile − padding alone. That was WRONG — the
 * 2px is this tile's own `border` — and the error propagated out of this
 * comment into three downstream documents before it was caught (OKS-972).
 * A comment written to warn that arithmetic disagrees with measurement
 * taught the very mistake it existed to prevent; the fix is the missing
 * term, not a wider tolerance.
 * See tests/playwright/badge-tile-clearance-oks926.
 */
export function BadgeSectionCard({
  title,
  count,
  emptyMessage,
  children,
  className,
}: {
  title: string
  count: number
  emptyMessage?: string
  children?: ReactNode
  className?: string
}) {
  const isEmpty = count === 0

  return (
    <Card
      data-testid="badge-section-card"
      className={cn("gap-4 p-4", className)}
    >
      <h2 className="text-muted-foreground text-xs font-semibold tracking-wide uppercase">
        {title} · {count}
      </h2>

      {isEmpty && emptyMessage ? (
        <p
          data-testid="badge-section-empty"
          className="text-muted-foreground text-sm"
        >
          {emptyMessage}
        </p>
      ) : (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-2">
          {children}
        </div>
      )}
    </Card>
  )
}
